/**
 * TanStack Query hooks for the admin-user CRUD endpoints (SPEC sections 5 + 9).
 *
 * Wraps:
 *   GET    /api/v1/admin/users                    -> useAdminUsers
 *   GET    /api/v1/admin/users/{id}               -> useAdminUserDetail
 *   POST   /api/v1/admin/users                    -> useCreateAdminUser
 *   PUT    /api/v1/admin/users/{id}               -> useUpdateAdminUser
 *   PUT    /api/v1/admin/users/{id}/permissions   -> useUpdateAdminUserPermissions
 *   POST   /api/v1/admin/users/{id}/resend-setup  -> useResendSetupEmail
 *   DELETE /api/v1/admin/users/{id}               -> useDeleteAdminUser
 *
 * All mutations invalidate the list query key so the table picks up the
 * server-side change without an explicit refetch from the caller.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  AdminUserCreateRequest,
  AdminUserListResponse,
  AdminUserPermissionsUpdateRequest,
  AdminUserResponse,
  AdminUserUpdateRequest,
  SystemRole,
} from "@/types/api";

export const ADMIN_USERS_QUERY_KEY = ["adminUsers"] as const;

export interface AdminUsersFilters {
  q?: string;
  role?: SystemRole;
  /** Restrict to staff roles (agent + system_admin). Used by the
   * Team tab in Settings; mutually exclusive with ``role`` server-side. */
  staff_only?: boolean;
  is_active?: boolean;
  include_deleted?: boolean;
  page?: number;
  page_size?: number;
}

/**
 * Build the params object sent to the backend. Omitting empty values
 * keeps the URL clean and lets the backend defaults take effect.
 */
function buildListParams(
  filters: AdminUsersFilters,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.q && filters.q.length > 0) params.q = filters.q;
  if (filters.role) params.role = filters.role;
  if (filters.staff_only) params.staff_only = true;
  if (filters.is_active !== undefined) params.is_active = filters.is_active;
  if (filters.include_deleted) params.include_deleted = true;
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.page_size !== undefined) params.page_size = filters.page_size;
  return params;
}

export function useAdminUsers(
  filters: AdminUsersFilters,
): UseQueryResult<AdminUserListResponse> {
  return useQuery({
    queryKey: [...ADMIN_USERS_QUERY_KEY, filters],
    queryFn: async () => {
      const response = await api.get<AdminUserListResponse>("/admin/users", {
        params: buildListParams(filters),
      });
      return response.data;
    },
  });
}

export function useAdminUserDetail(
  id: string | null,
): UseQueryResult<AdminUserResponse> {
  return useQuery({
    queryKey: [...ADMIN_USERS_QUERY_KEY, "detail", id],
    enabled: id !== null,
    queryFn: async () => {
      const response = await api.get<AdminUserResponse>(`/admin/users/${id}`);
      return response.data;
    },
  });
}

export function useCreateAdminUser(): UseMutationResult<
  AdminUserResponse,
  Error,
  AdminUserCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminUserCreateRequest) => {
      const response = await api.post<AdminUserResponse>(
        "/admin/users",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export interface UpdateAdminUserArgs {
  id: string;
  body: AdminUserUpdateRequest;
}

export function useUpdateAdminUser(): UseMutationResult<
  AdminUserResponse,
  Error,
  UpdateAdminUserArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const response = await api.put<AdminUserResponse>(
        `/admin/users/${id}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export interface UpdateAdminUserPermissionsArgs {
  id: string;
  body: AdminUserPermissionsUpdateRequest;
}

export function useUpdateAdminUserPermissions(): UseMutationResult<
  AdminUserResponse,
  Error,
  UpdateAdminUserPermissionsArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const response = await api.put<AdminUserResponse>(
        `/admin/users/${id}/permissions`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export function useResendSetupEmail(): UseMutationResult<
  AdminUserResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<AdminUserResponse>(
        `/admin/users/${id}/resend-setup`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

export function useDeleteAdminUser(): UseMutationResult<
  AdminUserResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete<AdminUserResponse>(
        `/admin/users/${id}`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
    },
  });
}

/**
 * Extract a backend `detail` error message from an Axios error. The
 * admin-user endpoints return human-readable strings on 400 / 404 / 409
 * (for example "You cannot delete your own account.") that should
 * surface verbatim in the UI.
 */
export function extractDetailMessage(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null) {
    const response = (err as { response?: { data?: { detail?: unknown } } })
      .response;
    const detail = response?.data?.detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
  }
  if (err instanceof Error && err.message.length > 0) return err.message;
  return fallback;
}
